import { useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import type { DraftVersion } from "../lib/drafts";

interface VersionHistoryProps {
  versions: DraftVersion[];
  onRestore: (version: DraftVersion) => void;
}

export function VersionHistory({ versions, onRestore }: VersionHistoryProps) {
  const [expanded, setExpanded] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);

  if (versions.length === 0) return null;

  return (
    <div className="mt-4 border rounded-lg bg-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 text-sm font-medium hover:bg-accent/50 transition-colors rounded-lg"
      >
        <span>Version History ({versions.length})</span>
        <span className="text-muted-foreground">{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        <div className="border-t divide-y">
          {versions.map((v) => {
            const isPreview = previewId === v.id;
            const previewContent = v.markdownSource || v.content || "";

            return (
              <div key={v.id}>
                <div className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="flex-1">
                    <span className="font-medium">{v.label}</span>
                    <span className="text-muted-foreground ml-2">
                      {new Date(v.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className={
                        v.status === "published"
                          ? "bg-emerald-500/10 text-emerald-400"
                          : v.status === "structured"
                          ? "bg-blue-500/10 text-blue-400"
                          : ""
                      }
                    >
                      {v.status.charAt(0).toUpperCase() + v.status.slice(1)}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPreviewId(isPreview ? null : v.id)}
                      className={isPreview ? "bg-accent" : ""}
                    >
                      {isPreview ? "Hide" : "Preview"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRestore(v)}
                    >
                      Restore
                    </Button>
                  </div>
                </div>
                {isPreview && (
                  <div className="p-3 text-sm leading-relaxed whitespace-pre-wrap border-t bg-muted/30 max-h-[300px] overflow-y-auto text-muted-foreground">
                    {previewContent}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
